import type { Point, ShapeStyle } from '../../state/types';
import { Rectangle } from '../../shapes/Rectangle';
import { RECOGNITION_CONFIG } from '../config';
import type { BoundingBox } from '../geometry';
import { calculateEdgeScore, findCorners, getAngle } from '../geometry';
import type { RecognitionResult } from '../types';

export interface RectangleRecognitionInput {
  simplified: Point[];
  bounds: BoundingBox;
  style?: Partial<ShapeStyle>;
  corners?: Point[];
}

export const recognizeRectangle = ({
  simplified,
  bounds,
  style,
  corners: providedCorners,
}: RectangleRecognitionInput): RecognitionResult => {
  const corners = providedCorners ?? findCorners(simplified, bounds);

  if (corners.length >= 3) {
    let rightAngleCount = 0;
    for (let i = 0; i < corners.length; i++) {
      const prev = corners[(i - 1 + corners.length) % corners.length]!;
      const curr = corners[i]!;
      const next = corners[(i + 1) % corners.length]!;

      const angle = getAngle(prev, curr, next);
      if (Math.abs(angle - 90) < RECOGNITION_CONFIG.rightAngleTolerance) {
        rightAngleCount++;
      }
    }

    const confidence = Math.min(
      RECOGNITION_CONFIG.rectangleMaxConfidence,
      RECOGNITION_CONFIG.rectangleCornerBaseConfidence +
      (rightAngleCount / 4) * RECOGNITION_CONFIG.rectangleCornerScale
    );
    return createRectangleResult(bounds, style, confidence);
  }

  const edgeScore = calculateEdgeScore(simplified, bounds);
  if (edgeScore > RECOGNITION_CONFIG.rectangleEdgeScoreThreshold) {
    return createRectangleResult(
      bounds,
      style,
      edgeScore * RECOGNITION_CONFIG.rectangleEdgeScoreScale
    );
  }

  return { type: 'rectangle', confidence: RECOGNITION_CONFIG.rectangleFallbackConfidence };
};

export const createRectangleResult = (
  bounds: BoundingBox,
  style?: Partial<ShapeStyle>,
  confidence = RECOGNITION_CONFIG.rectangleDefaultConfidence
): RecognitionResult => {
  const padding = RECOGNITION_CONFIG.rectanglePadding;
  const shape = new Rectangle(
    { x: bounds.minX - padding / 2, y: bounds.minY - padding / 2 },
    { width: bounds.width + padding, height: bounds.height + padding },
    style
  );
  return { type: 'rectangle', confidence, shape };
};
